/* eslint-disable import/no-cycle */
import { get, uniq, compact } from 'lodash';
import {
  financeService,
  MS_PHASE_3_ENDPOINTS,
} from 'utils/msEndpointConstants';
import {
  retrieveListOrObject,
  retrieveSortForMS,
  retrieveObjectMapper,
} from 'utils';
import { DEFAULT_ERROR, DATE_PICKER_FORMAT } from 'utils/constants';
import DatadogHandler from 'utils/datadog';
import {
  transformInvoiceItmes,
  transformInvoices,
  transformCreditDebitNotes,
} from 'utils/ms-utils/ms-phase-3/InvoiceUtils';
import moment from 'moment';
import { getReceiptByInvoiceIds } from './FinanceService';
import { executePromiseMap } from '../utils/ms-utils/ms-phase-3/CommonUtils';

const fileName = 'src/services/InvoiceService.js';
const { FINANCE, INVOICE } = MS_PHASE_3_ENDPOINTS;

export const getInvoicesByIds = (invoiceIds = []) => {
  if (invoiceIds.length === 0) return Promise.resolve([]);
  return financeService()
    .post(...FINANCE.getInvoicesByIds(invoiceIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const msGetCreditDebitNotesByInvoiceIds = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...INVOICE.getCreditDebitNotesByInvoiceIds(reqData)
    );
    const creditDebitNotes = transformCreditDebitNotes(resp?.data?.data || {});
    return {
      success: true,
      data: creditDebitNotes,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetCreditDebitNotesByInvoiceIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetInvoiceItemByIds = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...INVOICE.getInvoiceItemsByInvoiceIds(reqData)
    );

    const data = resp?.data?.data ?? [];
    const fkBillableItems = compact(
      uniq(data?.map(item => item?.fk_billable_item) ?? [])
    );
    if (fkBillableItems.length === 0) {
      return {
        success: true,
        data: { invoiceItems: { data: [], totalCount: 0 } },
      };
    }

    const resp2 = await financeSvc.post(
      ...INVOICE.getBillableItemsByIds({ billable_item_ids: fkBillableItems })
    );
    const billableItems = resp2?.data?.data ?? [];
    const invoiceItems = transformInvoiceItmes(data, billableItems);

    return {
      success: true,
      data: {
        invoiceItems: {
          data: invoiceItems,
          totalCount: resp?.data?.totalCount || 0,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetInvoiceItemByIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateOfflineInvoice = async (_, reqData) => {
  try {
    const reqBody = {
      centre_id: reqData?.IDCentre,
      child_id: reqData?.IDChild,
      invoice_items: reqData?.items?.map(item => ({
        ForMonth: moment(item?.ForMonth).format(DATE_PICKER_FORMAT),
        IDBillableItem: item?.IDBillableItem,
        Quantity: item?.Quantity,
        UnitPrice: item?.UnitPrice,
      })),
      remark: reqData?.remark,
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...INVOICE.createOfflineInvoice(reqBody)
    );
    return {
      success: true,
      data: {
        createOfflineInvoice: {
          ...retrieveObjectMapper(resp?.data || {}, [
            'ID:id',
            'label',
            'total_amount',
          ]),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateOfflineInvoice',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchInvoiceByFkChild = async (_, reqData) => {
  try {
    const { pagination, filter } = reqData;
    let params = {};
    if (pagination?.page) {
      params.page = pagination?.page;
      params.perPage = pagination?.perPage;
    }
    if (pagination?.sort) {
      params = {
        ...params,
        ...retrieveSortForMS(pagination?.sort, 'customSort'),
      };
    }

    const reqBody = {
      child_id: reqData?.IDChild,
      ...(filter?.fkCentre && {
        filter: { fk_centre: [{ eq: filter?.fkCentre }] },
      }),
      ...(filter?.status && {
        filter: { status: [{ eq: filter?.status }] },
      }),
      ...(filter?.ID && {
        filter: { id: [{ eq: filter?.ID }] },
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...INVOICE.getInvoiceByChildId(reqBody, params)
    );

    const data = resp?.data?.data ?? [];
    const invoiceIds = compact(uniq(data?.map(item => item?.id) ?? []));

    if (invoiceIds.length === 0) {
      return {
        success: true,
        data: { invoicesByFkChild: { data: [], totalCount: 0 } },
      };
    }

    const promiseMap = {};
    promiseMap.invoiceItems = msGetInvoiceItemByIds(null, {
      invoice_ids: invoiceIds,
    });
    promiseMap.creditDebitNotes = msGetCreditDebitNotesByInvoiceIds(null, {
      invoiceIds,
    });
    promiseMap.receipts = getReceiptByInvoiceIds(invoiceIds);

    const { invoiceItems, creditDebitNotes, receipts } =
      await executePromiseMap(promiseMap);

    const invoices = transformInvoices({
      invoices: data,
      creditDebitNotes: creditDebitNotes?.data ?? {},
      invoiceItems: invoiceItems?.data?.invoiceItems?.data ?? [],
      receipts: receipts?.data ?? {},
    });
    return {
      success: true,
      data: {
        invoicesByFkChild: {
          data: invoices,
          totalCount: resp?.data?.totalCount || 0,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchInvoiceByFkChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msValidateAndCreateBulkInvoiceRequest = async (_, reqData) => {
  try {
    if (!reqData?.filePath) {
      return {
        success: true,
        data: {},
      };
    }
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...INVOICE.validateBulkInvoiceRequest(reqData?.filePath)
    );
    return {
      success: true,
      data: {
        validateBulkInvoiceRequest: resp?.data || {},
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msValidateAndCreateBulkInvoiceRequest',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetBulkInvoiceTemplate = async () => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.get(INVOICE.getBulkInvoiceTemplate());
    return {
      success: true,
      data: {
        getBulkInvoiceTemplate: resp?.data?.path || '',
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetBulkInvoiceTemplate',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR,
          errCode: ex?.response?.data?.errCode || ex?.code,
        },
      ],
    };
  }
};

export const msUpdateBulkInvoiceScheduledTime = async (_, reqData) => {
  try {
    if (!reqData?.filePath) {
      return {
        success: true,
        data: {},
      };
    }
    const reqBody = {
      file_path: reqData?.filePath,
      ...(reqData?.scheduledAt && {
        scheduled_at: reqData?.scheduledAt,
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...INVOICE.createBulkInvoiceRequest(reqBody)
    );
    return {
      success: true,
      data: {
        createBulkInvoiceRequest: resp?.data?.id && { ID: resp?.data?.id },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpdateBulkInvoiceScheduledTime',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR,
          errCode: ex?.response?.data?.errCode || ex?.code,
        },
      ],
    };
  }
};

export const msGetBulkInvoiceUploadURL = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...INVOICE.getBulkInvoiceUploadURL({
        file_name: get(reqData, 'file.fileName'),
        file_path: get(reqData, 'file.filePath'),
        file_size: get(reqData, 'file.fileSize'),
        mime_type: get(reqData, 'file.mimeType'),
      })
    );
    return {
      success: true,
      data: {
        getBulkInvoiceUploadURL: retrieveObjectMapper(resp?.data, [
          'file_name',
          'Key:key',
          'Url:url',
        ]),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetBulkInvoiceUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR,
          errCode: ex?.response?.data?.errCode || ex?.code,
        },
      ],
    };
  }
};

export const getBillableItemsByIds = async (ids = []) => {
  if (ids.length === 0) return [];
  return financeService()
    .post(
      ...INVOICE.getBillableItemsByIds({
        billable_item_ids: ids,
      })
    )
    .then(retrieveListOrObject)
    .catch(() => []);
};

export default {
  getInvoicesByIds,
  msGetBulkInvoiceTemplate,
};
